/* V218.7 — IndexNow admin dashboard.
 *
 * Manual "Run batch" trigger + history of recent IndexNow push batches.
 * Pushes every URL from /sitemap.xml to Bing, Yandex, Seznam, plus pings
 * Google + Bing sitemap endpoints (gentle fallback after Google deprecated
 * the official /ping in 2023).
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function StatusPill({ ok, status }) {
  return (
    <span
      className={
        "inline-block px-2 py-0.5 rounded text-[10px] font-mono " +
        (ok ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-rose-500/15 text-rose-300 border border-rose-500/30")
      }
    >
      {ok ? "OK" : "FAIL"} · {status}
    </span>
  );
}

export default function AdminIndexNowTab() {
  const [history, setHistory] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState("");

  const token = () => localStorage.getItem("admin_token") || "";

  const loadHistory = async () => {
    if (!token()) { setErr("Admin token chýba."); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/index-now/admin/history`, {
        headers: { "X-Admin-Token": token() },
        params: { limit: 30 },
      });
      setHistory(r.data);
      setErr("");
    } catch (e) {
      setErr(e.response?.data?.detail || "load_failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line
  }, []);

  const runBatch = async () => {
    if (!token() || running) return;
    if (!window.confirm("Spustiť IndexNow batch? Stiahne sitemap a pošle všetkých ~40 000 URL-iek do Bing/Yandex/Seznam. Trvá ~30 sekúnd.")) return;
    setRunning(true);
    setErr("");
    try {
      const origin = typeof window !== "undefined" ? window.location.origin : undefined;
      const r = await axios.post(
        `${API}/index-now/admin/run-batch`,
        null,
        {
          headers: { "X-Admin-Token": token() },
          params: { origin, max_urls: 10000 },
          timeout: 60000,
        }
      );
      setLastRun(r.data);
      await loadHistory();
    } catch (e) {
      setErr(e.response?.data?.detail || "run_failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 space-y-6" data-testid="admin-indexnow-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-white">🔎 IndexNow — bezplatné inštantné indexovanie</h2>
          <p className="text-zinc-400 text-sm mt-1 max-w-2xl">
            Otvorený štandard od Microsoft/Bing. Pošli URL → Bing, Yandex, Seznam.cz a Naver
            ich začnú crawlovať do hodín. Google IndexNow nevyužíva — pre Google používame
            sitemap referenciu v robots.txt + GSC.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="indexnow-refresh"
            onClick={loadHistory}
            disabled={loading}
            className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 text-zinc-300 text-xs rounded hover:bg-zinc-800 disabled:opacity-50"
          >
            ↻ Obnoviť
          </button>
          <button
            data-testid="indexnow-run-batch"
            onClick={runBatch}
            disabled={running}
            className="px-4 py-1.5 bg-amber-500 text-black text-xs font-bold rounded hover:bg-amber-400 disabled:opacity-50"
          >
            {running ? "⏳ Pushujem..." : "🚀 Spustiť batch push"}
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-rose-950/40 border border-rose-700 text-rose-300 p-3 text-sm rounded">
          {err}
        </div>
      )}

      {history?.key_url_pattern && (
        <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-4 text-sm">
          <div className="text-zinc-400 text-xs uppercase tracking-wider mb-2">Verification URL (IndexNow ownership)</div>
          <code className="text-emerald-300 break-all" data-testid="indexnow-key-url">
            {window.location.origin}{history.key_url_pattern}
          </code>
          <p className="text-zinc-500 text-xs mt-2">
            Tento endpoint Bing/Yandex/Seznam GETuje aby si overili že kľúč je tvoj. Nech je dostupný 24/7.
          </p>
        </div>
      )}

      {lastRun && (
        <div className="bg-emerald-950/30 border border-emerald-700/40 rounded-xl p-4 space-y-3" data-testid="indexnow-last-run">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-bold text-emerald-300">Posledný batch push</h3>
            <span className="text-xs font-mono text-zinc-400">
              {lastRun.submitted.toLocaleString("sk-SK")} URL z {lastRun.sitemap_url}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {lastRun.indexnow_engines?.map((e, i) => (
              <div key={i} className="bg-zinc-900 px-3 py-2 rounded border border-zinc-800 text-xs flex items-center gap-2">
                <span className="font-mono text-zinc-300">{new URL(e.endpoint).hostname}</span>
                <StatusPill ok={e.ok} status={e.status} />
              </div>
            ))}
            {lastRun.google_ping && (
              <div className="bg-zinc-900 px-3 py-2 rounded border border-zinc-800 text-xs flex items-center gap-2">
                <span className="font-mono text-zinc-300">google.com/ping</span>
                <StatusPill ok={lastRun.google_ping.ok} status={lastRun.google_ping.status} />
              </div>
            )}
            {lastRun.bing_ping && (
              <div className="bg-zinc-900 px-3 py-2 rounded border border-zinc-800 text-xs flex items-center gap-2">
                <span className="font-mono text-zinc-300">bing.com/ping</span>
                <StatusPill ok={lastRun.bing_ping.ok} status={lastRun.bing_ping.status} />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="space-y-3">
        <h3 className="text-sm font-bold text-zinc-200">História posledných batchov</h3>
        {!history || history.batches?.length === 0 ? (
          <div className="text-zinc-500 text-sm border border-dashed border-zinc-800 rounded-lg p-4">
            Žiadne batche zatiaľ. Klikni "🚀 Spustiť batch push" hore.
          </div>
        ) : (
          history.batches.map((b, i) => (
            <div
              key={i}
              className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-3"
              data-testid={`indexnow-batch-${i}`}
            >
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div className="text-xs font-mono text-zinc-400">
                  {new Date(b.submitted_at).toLocaleString("sk-SK")}
                  <span className="ml-2 text-zinc-500">·</span>
                  <span className="ml-2 text-zinc-300">{b.url_count?.toLocaleString("sk-SK")} URL</span>
                  <span className="ml-2 text-zinc-500">·</span>
                  <span className="ml-2 text-zinc-500">{b.trigger}</span>
                </div>
                <div className="text-xs text-zinc-500 font-mono">{b.host}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {b.engines?.map((e, j) => (
                  <div key={j} className="text-[10px] font-mono flex items-center gap-1">
                    <span className="text-zinc-500">{new URL(e.endpoint).hostname}:</span>
                    <StatusPill ok={e.ok} status={e.status} />
                  </div>
                ))}
                {b.google_ping && (
                  <div className="text-[10px] font-mono flex items-center gap-1">
                    <span className="text-zinc-500">google:</span>
                    <StatusPill ok={b.google_ping.ok} status={b.google_ping.status} />
                  </div>
                )}
                {b.bing_ping && (
                  <div className="text-[10px] font-mono flex items-center gap-1">
                    <span className="text-zinc-500">bing:</span>
                    <StatusPill ok={b.bing_ping.ok} status={b.bing_ping.status} />
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
